import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { INTL_DMY_CASUAL_FORMAT, INTL_LONG_WEEKDAY } from 'constants/dateFormats'

// LuxKit Components
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import Heading from 'LuxKit/Typography/Heading'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import MaldivesWasPriceRow from './MaldivesWasPriceRow'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import LineArrowRightIcon from 'LuxKit/Icons/line/LineArrowRightIcon'
import SolidExclamationCircleIcon from 'LuxKit/Icons/solid/SolidExclamationCircleIcon'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import { type FlightsClassTypes, type FareClassWithReward } from 'constants/flight'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
// Hooks
import { useCalendarItemData } from '../hooks/useCalendarItemData'
import { useCharterRewardAvailabilityContext } from '../hooks/charterRewardAvailabilityContext'
import type { CalendarItemBundleData } from '../hooks/types'
import type { MaldivesCabinHotelEntry } from '../hooks/useMaldivesOffersPricing'
import useCalendarItemPricing from './useCalendarItemPricing'
import { computeFareClassCellPrice } from './maldivesCharterTripOptionsTablePricing'
import { getCharterSelectableCashCabins } from 'lib/maldivesCharter/charterCabins'
import { useAppSelector } from 'hooks/reduxHooks'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { useSeatEligiblePassengers } from '../hooks/utils'
import MaldivesFnfSpecialOffer from './MaldivesFnfSpecialOffer'
import MaldivesCharterRewardSeatsAvailableLabel from './MaldivesCharterRewardSeatsAvailableLabel'
import MaldivesCharterDateFareCarousel from './MaldivesCharterDateFareCarousel'
import MaldivesCharterDateRowFareToggle from './MaldivesCharterDateRowFareToggle'
import type { MaldivesCharterTripOption } from './MaldivesCharterFareCarouselOption'

// Utils
import { MALDIVES_CHARTER_ROUTE } from 'api/lib/flightDealMap'
import KidsFlyFreeLabel from 'components/Common/Labels/KidsFlyFreeLabel'
import useKidsFlyFree from 'hooks/CharterFlights/useKidsFlyFree'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'

// Types
interface TripOption {
  id: string
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  price: number
  memberPrice?: number
  currency: string
  seatsLeft?: number
  nights: number
  available: boolean
  fareClass?: FareClassWithReward
}

interface Props {
  departDate: string
  returnDate: string
  checkIn: string
  checkOut: string
  nights: number
  rooms: Array<App.Occupants>
  fareClass: FlightsClassTypes
  selectedFareClass?: FareClassWithReward | null
  priceMode: 'total' | 'perPerson'
  luxPlusEnabled: boolean
  isSelected: boolean
  onSelect: (option: TripOption) => void
  prefetchedBundleData?: CalendarItemBundleData
  /** Available fare classes (including reward variants) for the per-date fare carousel. */
  allowedFareClasses?: ReadonlyArray<FareClassWithReward>
  /** Callback when the user picks a different fare class from the carousel. */
  onFareClassChange?: (fareClass: FareClassWithReward) => void
  feederOriginAirportCode?: string
  /** When true, show reward pricing on the collapsed date card. */
  rewardSeatsOnly?: boolean
  /** Cabin filter from the dropdown — determines which cabin's price to show on the collapsed card. */
  cabinFilter?: FlightsClassTypes | null
  hotelPriceByCabinAndDate?: Partial<Record<FlightsClassTypes, Record<string, MaldivesCabinHotelEntry>>>
}

// Styled Components - Mobile specific layout
const DateCard = styled.div<{ $clickable?: boolean }>`
  padding: ${rem(12)} ${rem(20)};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  ${p => p.$clickable && 'cursor: pointer;'}
`

const SelectButton = styled(TextButton)`
  min-width: ${rem(80)};
`

const DateRangeContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  align-items: center;
`

const SmallSup = styled.sup`
  font-size: 0.6em;
  vertical-align: super;
`

function MaldivesDirectSearchFlightCalendarItemMobile(props: Props) {
  const {
    departDate,
    returnDate,
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    selectedFareClass,
    priceMode,
    luxPlusEnabled,
    isSelected,
    onSelect,
    prefetchedBundleData,
    allowedFareClasses,
    onFareClassChange,
    feederOriginAirportCode,
    rewardSeatsOnly = false,
    cabinFilter,
    hotelPriceByCabinAndDate,
  } = props

  const intl = useIntl()
  const [fareCarouselExpanded, setFareCarouselExpanded] = useState(false)
  const seatEligiblePassengers = useSeatEligiblePassengers(rooms)
  const totalPassengers = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const isLuxRowEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.luxRow)
  const selectableCashCabins = useMemo(
    () => getCharterSelectableCashCabins(isLuxRowEnabled),
    [isLuxRowEnabled],
  )
  const { isKidsFlyFreeDate } = useKidsFlyFree({
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    occupancies: rooms,
    fareClass,
  })

  const fetchedBundleData = useCalendarItemData({
    checkIn,
    checkOut,
    nights,
    rooms,
    fareClass,
    priceMode,
    charterBundleRoute: MALDIVES_CHARTER_ROUTE,
    feederOriginAirportCode,
    disabled: !!prefetchedBundleData,
  })

  const bundleData = prefetchedBundleData ?? fetchedBundleData

  const { isAvailableForDate: isRewardAvailableForDate, getLegResult, getJourneysForCabinDate } = useCharterRewardAvailabilityContext()
  const showRewardSeatsLabel = isRewardAvailableForDate(checkIn)

  // Unified collapsed-card price — same logic as the desktop trip options table:
  // 1. cabinFilter set → that cabin's cash price (via hotelPriceByCabinAndDate + per-cabin journeys)
  // 2. cabinFilter null → fall through to bundleData (all-cabin cheapest)
  // 3. Rewards ON → cheapest reward price across visible cabins, or undefined (= "No seats available")
  const { bestCashPrice, bestRewardPrice } = useMemo(() => {
    const cabinsToCheck = cabinFilter ? [cabinFilter] : selectableCashCabins
    let bestCash: { cashTotal: number; cashMemberTotal?: number } | undefined
    let bestReward: { pointsTotal: number; cashTotal: number; cashMemberTotal?: number } | undefined

    // Cash: only compute per-cabin when cabinFilter is set; when null, cabinCash stays undefined → falls back to bundleData
    if (cabinFilter) {
      const journeys = getJourneysForCabinDate(cabinFilter, checkIn)
      if (journeys?.departing && journeys?.returning) {
        // Hotel: prefer per-cabin data, fall back to bundleData fields
        const cabinHotel = hotelPriceByCabinAndDate?.[cabinFilter]?.[checkIn]
        const hotelPrice = cabinHotel
          ? (luxPlusEnabled ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0) : (cabinHotel.hotelPrice || 0))
          : (bundleData.hotelPrice ?? (bundleData.flightPrice != null ? Math.max(0, bundleData.totalPrice - bundleData.flightPrice) : undefined))
        const hotelMemberPrice = cabinHotel?.hotelMemberPrice ?? bundleData.hotelMemberPrice
        const price = computeFareClassCellPrice({
          fareClass: cabinFilter,
          hotelPrice,
          hotelMemberPrice,
          departingFlight: journeys.departing,
          returningFlight: journeys.returning,
          seatEligiblePassengers,
          getLegResult,
        })
        if (price.cashTotal != null) {
          bestCash = { cashTotal: price.cashTotal, cashMemberTotal: price.cashMemberTotal }
        }
      }
    }

    // Reward: scan visible cabins inline (same as desktop), using hotelPriceByCabinAndDate first
    for (const cabin of cabinsToCheck) {
      const journeys = getJourneysForCabinDate(cabin, checkIn)
      if (!journeys?.departing || !journeys?.returning) continue

      if (!journeys.departing.journeyKey || !journeys.returning.journeyKey) continue
      const depResult = getLegResult(journeys.departing.journeyKey)
      const retResult = getLegResult(journeys.returning.journeyKey)
      if (!depResult?.available || !retResult?.available) continue
      if (depResult.pointsPrice == null || retResult.pointsPrice == null) continue
      if (depResult.cashPrice == null || retResult.cashPrice == null) continue

      const cabinHotel = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
      let hotelForReward = 0
      if (cabinHotel) {
        hotelForReward = luxPlusEnabled
          ? (cabinHotel.hotelMemberPrice || cabinHotel.hotelPrice || 0)
          : (cabinHotel.hotelPrice || 0)
      } else if (bundleData.available) {
        const cabinFlightFare = (journeys.departing.price?.all?.totalFare ?? 0)
          + (journeys.returning.price?.all?.totalFare ?? 0)
          + (journeys.departing.feederEstimatedPrice ?? 0)
          + (journeys.returning.feederEstimatedPrice ?? 0)
        const bundleTotal = luxPlusEnabled ? (bundleData.memberTotalPrice || bundleData.totalPrice || 0) : (bundleData.totalPrice || 0)
        hotelForReward = Math.max(0, bundleTotal - cabinFlightFare)
      }
      if (!hotelForReward) continue

      const rawPoints = (depResult.pointsPrice + retResult.pointsPrice) * seatEligiblePassengers
      const rawCash = (depResult.cashPrice + retResult.cashPrice) * seatEligiblePassengers + hotelForReward
      const totalPoints = priceMode === 'perPerson' && totalPassengers > 0 ? rawPoints / totalPassengers : rawPoints
      const totalCash = priceMode === 'perPerson' && totalPassengers > 0 ? rawCash / totalPassengers : rawCash

      if (!bestReward || totalCash < bestReward.cashTotal) {
        bestReward = { pointsTotal: totalPoints, cashTotal: totalCash }
      }
    }

    return { bestCashPrice: bestCash, bestRewardPrice: bestReward }
  }, [cabinFilter, selectableCashCabins, getJourneysForCabinDate, checkIn, hotelPriceByCabinAndDate, seatEligiblePassengers, getLegResult, luxPlusEnabled, bundleData, priceMode, totalPassengers])

  // Preview pricing for logged-out / non-member users: hardcoded rate + hotel from bundleData
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const hasLoyaltyAccount = useAppSelector(getHasLoyaltyAccount)
  const rewardLockedForLogin = rewardSeatsOnly && (!memberId || !hasLoyaltyAccount)
  const rewardPreviewPrice = useMemo(() => {
    // Only show preview when the cabin actually has availability for this date
    const cabinAvailable = cabinFilter ? !!bestCashPrice : bundleData.available
    if (!rewardLockedForLogin || !cabinAvailable) return undefined
    const cabinsToCheck = cabinFilter ? [cabinFilter] : selectableCashCabins
    let cheapestRate: { pointsPerPerson: number; cashPerPerson: number } | undefined
    for (const cabin of cabinsToCheck) {
      const rate = REWARD_PREVIEW_RATE_BY_CABIN[cabin]
      if (rate && (!cheapestRate || rate.cashPerPerson < cheapestRate.cashPerPerson)) {
        cheapestRate = rate
      }
    }
    if (!cheapestRate) return undefined
    // Hotel from hotelPriceByCabinAndDate or bundleData
    let hotelPrice = 0
    for (const cabin of cabinsToCheck) {
      const h = hotelPriceByCabinAndDate?.[cabin]?.[checkIn]
      const hp = h ? (luxPlusEnabled ? (h.hotelMemberPrice || h.hotelPrice || 0) : (h.hotelPrice || 0)) : 0
      if (hp > 0 && (!hotelPrice || hp < hotelPrice)) hotelPrice = hp
    }
    if (!hotelPrice && bundleData.hotelPrice) hotelPrice = bundleData.hotelPrice
    if (!hotelPrice) return undefined
    const rawPoints = cheapestRate.pointsPerPerson * seatEligiblePassengers
    const rawCash = hotelPrice + cheapestRate.cashPerPerson * seatEligiblePassengers
    return {
      pointsTotal: priceMode === 'perPerson' && totalPassengers > 0 ? rawPoints / totalPassengers : rawPoints,
      cashTotal: priceMode === 'perPerson' && totalPassengers > 0 ? rawCash / totalPassengers : rawCash,
    }
  }, [rewardLockedForLogin, bundleData.available, bundleData.hotelPrice, cabinFilter, selectableCashCabins, bestCashPrice, hotelPriceByCabinAndDate, checkIn, luxPlusEnabled, seatEligiblePassengers, priceMode, totalPassengers])

  // Override bundleData with the per-cabin cash price so useCalendarItemPricing picks it up
  const effectiveBundleData = useMemo(() => {
    // When a cabin is selected but has no availability, mark as unavailable
    // instead of falling back to the page-level fare's price
    if (!bestCashPrice && cabinFilter) {
      return { ...bundleData, available: false, totalPrice: 0, pricePerPerson: 0, memberTotalPrice: undefined, memberPricePerPerson: undefined }
    }
    if (!bestCashPrice) return bundleData
    const totalPassengers = Math.max(1, seatEligiblePassengers)
    return {
      ...bundleData,
      totalPrice: bestCashPrice.cashTotal,
      memberTotalPrice: bestCashPrice.cashMemberTotal,
      pricePerPerson: bestCashPrice.cashTotal / totalPassengers,
      memberPricePerPerson: bestCashPrice.cashMemberTotal ? bestCashPrice.cashMemberTotal / totalPassengers : undefined,
    }
  }, [bestCashPrice, cabinFilter, bundleData, seatEligiblePassengers])

  const {
    regularPrice,
    memberPrice,
    isMemberPrice,
    displayPrice,
    wasPrice,
    shouldShowWasPrice,
    inventoryMessageType,
    inventoryMessage,
    isPromotionPriceApplied,
    fnfInventoryMessage,
    specialOfferLabel,
  } = useCalendarItemPricing({
    bundleData: effectiveBundleData,
    luxPlusEnabled,
    priceMode,
  })
  const saleLabel = isPromotionPriceApplied && !rewardSeatsOnly && displayPrice > 0 ? (
    <UrgencyLabel variant="critical2" icon={<SolidFireIcon />}>
      <FormattedMessage
        id="hwlzP_ydhpmzJAO7bbRv"
        defaultMessage="Sale"
        description="Urgency label shown on Maldives charter calendar fare cells when the flight price uses promotional sale fares"
      />
    </UrgencyLabel>
  ) : null
  const showInventoryMessage = inventoryMessage !== '' && (!bundleData.isFnfPriceApplied || inventoryMessageType !== 'seat')

  const option: TripOption = {
    id: `${checkIn}-${nights}n`,
    departDate: departDate,
    returnDate: returnDate,
    checkIn: checkIn,
    checkOut: checkOut,
    nights,
    price: regularPrice,
    memberPrice: memberPrice,
    currency: 'AUD',
    seatsLeft: bundleData.seatsLeft,
    available: Boolean(bundleData.available),
    fareClass: selectedFareClass ?? undefined,
  }

  const selectedText = intl.formatMessage({
    id: 'acuxSK1zMav7-Sn9mh4K',
    defaultMessage: 'Selected',
    description: 'Button label when a charter flight date is currently selected',
  })
  const unavailableText = intl.formatMessage({
    id: 'lbS33CL-vQm3bmd7lueV',
    defaultMessage: 'Sold out!',
    description: 'Short label when a fare column has no reward-seat inventory or no seats for that cabin',
  })
  const loadingText = intl.formatMessage({
    id: 'C6m8UbIIgveZbgrQtJ6T',
    defaultMessage: 'Loading...',
    description: 'Loading state text while charter flight prices are being fetched',
  })
  const nightsAndFlightsText = intl.formatMessage(
    {
      id: 'r484bkJ_LnqWqW8S-s9D',
      defaultMessage: '{nights, plural, one {# night} other {# nights}} & flights',
      description: 'Label showing number of nights and flights included in charter bundle',
    },
    { nights },
  )
  const dayOffsetText = intl.formatMessage({
    id: 'l9QfkSZq5TknjYVpdoEi',
    defaultMessage: '+1',
    description: 'Day offset indicator showing arrival is next day for charter flight',
  })
  const changeFareLabel = intl.formatMessage({
    id: 'Rt6uQBd5FYfbS4hAV4ab',
    defaultMessage: 'Change fare',
    description: 'Link to reopen the fare carousel for an already-selected Maldives charter date',
  })
  const expandAriaLabel = intl.formatMessage({
    id: '711wxG49umcK97pt8pS-',
    defaultMessage: 'Show fare options for these dates',
    description: 'Accessibility label for expanding the fare carousel beneath the row.',
  })
  const collapseAriaLabel = intl.formatMessage({
    id: '32x_r3aTH5F8f-0HHeLj',
    defaultMessage: 'Hide fare options for these dates',
    description: 'Accessibility label for collapsing the fare carousel shown below the row.',
  })
  const unavailableAriaLabel = intl.formatMessage({
    id: 'OwJEwq2JWiXn3Mtk7a4k',
    defaultMessage: 'Fare options unavailable for these dates',
    description: 'Accessibility label for the disabled fare-toggle control.',
  })

  // Render price display
  let priceDisplay
  if (bundleData.fetching) {
    priceDisplay = (
      <BodyText variant="small" colour="light-foreground-alt">
        {loadingText}
      </BodyText>
    )
  } else if (rewardSeatsOnly && bestRewardPrice) {
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '61Gm0owvNXkEHBOdcx8J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar item',
        })}
        </PriceRowPriceCaption>
        <Heading as="div" variant="heading4" colour={luxPlusEnabled ? 'lux-plus' : undefined} wrap="no-wrap">
          <FormatLoyaltyPoints abbreviated points={bestRewardPrice.pointsTotal} />
        </Heading>
        <Caption variant="medium" colour={luxPlusEnabled ? 'lux-plus' : 'neutral-two'} weight="normal">
          <FormattedMessage
            id="peHFjMVc2YKKQdYS2mU5"
            defaultMessage="+ {cash}"
            description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
            values={{ cash: <FormatCurrency value={bestRewardPrice.cashTotal} format="roundedDollar" /> }}
          />
        </Caption>
      </>
    )
  } else if (rewardSeatsOnly && !bestRewardPrice && rewardPreviewPrice) {
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '61Gm0owvNXkEHBOdcx8J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar item',
        })}
        </PriceRowPriceCaption>
        <Heading as="div" variant="heading4" colour={luxPlusEnabled ? 'lux-plus' : undefined} wrap="no-wrap">
          <FormatLoyaltyPoints abbreviated points={rewardPreviewPrice.pointsTotal} />
        </Heading>
        <Caption variant="medium" colour={luxPlusEnabled ? 'lux-plus' : 'neutral-two'} weight="normal">
          <FormattedMessage
            id="peHFjMVc2YKKQdYS2mU5"
            defaultMessage="+ {cash}"
            description="Cash co-pay portion of a Société reward-seat bundle in the Maldives charter calendar, shown under the points total in a smaller line with a leading plus sign before the formatted currency amount."
            values={{ cash: <FormatCurrency value={rewardPreviewPrice.cashTotal} format="roundedDollar" /> }}
          />
        </Caption>
      </>
    )
  } else if (rewardSeatsOnly && !bestRewardPrice) {
    priceDisplay = (
      <BodyText variant="medium" colour="neutral-three">
        {intl.formatMessage({
          id: 'lbS33CL-vQm3bmd7lueV',
          defaultMessage: 'Sold out!',
          description: 'Short label when a fare column has no reward-seat inventory or no seats for that cabin',
        })}
      </BodyText>
    )
  } else if (displayPrice > 0) {
    const perPersonLabel = intl.formatMessage({
      id: 'ew2ARy7ro3tFSM4inpHN',
      defaultMessage: '/person',
      description: 'Per person pricing unit suffix in Maldives calendar',
    })
    const totalLabel = intl.formatMessage({
      id: 'j--PuUYLTgWUxAxROh0Z',
      defaultMessage: 'total',
      description: 'Total pricing unit suffix in the Maldives calendar',
    })
    priceDisplay = (
      <>
        <PriceRowPriceCaption>{intl.formatMessage({
          id: '61Gm0owvNXkEHBOdcx8J',
          defaultMessage: 'From',
          description: 'Price prefix label in Maldives flight calendar item',
        })}
        </PriceRowPriceCaption>
        <div>
          <FormattedMessage
            id="eEOhCaC58xEuJ7A6ucxl"
            defaultMessage="{price} {unit}"
            description="Price amount followed by pricing unit (per person or total) in Maldives flight calendar mobile"
            values={{
              price: <Heading as="span" variant="heading4" colour={isMemberPrice ? 'lux-plus' : undefined} wrap="no-wrap">
                <FormatCurrency value={displayPrice} format="roundedDollar" />
              </Heading>,
              unit: <Caption as="span" variant="large" colour={isMemberPrice ? 'lux-plus' : 'light-foreground-alt'} weight="normal" format="lowercase">
                {priceMode === 'perPerson' ? perPersonLabel : totalLabel}
              </Caption>,
            }}
          />
        </div>
        {shouldShowWasPrice && wasPrice != null && <MaldivesWasPriceRow
          altPrice={wasPrice}
          saleUnit={priceMode === 'perPerson' ? '/person' : undefined}
        />}
      </>
    )
  } else {
    priceDisplay = (
      <BodyText variant="medium" colour="light-foreground-alt">
        {unavailableText}
      </BodyText>
    )
  }

  useEffect(() => {
    if (isSelected) {
      setFareCarouselExpanded(true)
    }
  }, [isSelected])

  const selectControl = useMemo(() => {
    if (isSelected && !fareCarouselExpanded) {
      return (
        <Group direction="vertical" gap={4} horizontalAlign="end">
          <SelectButton
            kind="primary"
            nonInteractable
            tabIndex={-1}
          >
            {selectedText}
          </SelectButton>
          <TextLink size="caption" weight="regular" onClick={() => setFareCarouselExpanded(true)}>
            {changeFareLabel}
          </TextLink>
        </Group>
      )
    }
    return (
      <MaldivesCharterDateRowFareToggle
        expanded={fareCarouselExpanded}
        disabled={!bundleData.available}
        onToggle={() => setFareCarouselExpanded(v => !v)}
        expandAriaLabel={expandAriaLabel}
        collapseAriaLabel={collapseAriaLabel}
        unavailableAriaLabel={unavailableAriaLabel}
      />
    )
  }, [isSelected, fareCarouselExpanded, bundleData.available, selectedText, changeFareLabel, expandAriaLabel, collapseAriaLabel, unavailableAriaLabel])

  const carouselTripOption: MaldivesCharterTripOption = useMemo(() => ({
    id: option.id,
    departDate: option.departDate,
    returnDate: option.returnDate,
    checkIn: option.checkIn,
    checkOut: option.checkOut,
    nights: option.nights,
    price: option.price,
    memberPrice: option.memberPrice,
    currency: option.currency,
    seatsLeft: option.seatsLeft,
    available: option.available,
  }), [option.id, option.departDate, option.returnDate, option.checkIn, option.checkOut, option.nights, option.price, option.memberPrice, option.currency, option.seatsLeft, option.available])

  const handleCarouselPickFare = useCallback((pickedFareClass: FareClassWithReward, pickedOption: MaldivesCharterTripOption) => {
    onFareClassChange?.(pickedFareClass)
    onSelect({
      ...pickedOption,
      fareClass: pickedFareClass,
    })
  }, [onSelect, onFareClassChange])

  return (
    <DateCard
      $clickable={bundleData.available}
      onClick={bundleData.available ? () => setFareCarouselExpanded(prev => !prev) : undefined}
    >
      <VerticalSpacer gap={12}>
        {saleLabel}

        {/* Dates Row */}
        <DateRangeContainer>
          <div>
            <BodyText variant="small" colour="light-foreground-alt">
              {intl.formatDate(departDate, INTL_LONG_WEEKDAY)}
            </BodyText>
            <Heading variant="heading5" wrap="no-wrap">
              {intl.formatDate(departDate, INTL_DMY_CASUAL_FORMAT)}
            </Heading>
          </div>
          <Group direction="vertical" gap={0} horizontalAlign="center">
            <LineArrowRightIcon size="M" />
            <BodyText variant="small" colour="light-foreground-alt">
              {nightsAndFlightsText}
            </BodyText>
          </Group>
          <Group direction="vertical" horizontalAlign="end">
            <Group direction="vertical" horizontalAlign="start" gap={0}>
              <BodyText variant="small" colour="light-foreground-alt">
                {intl.formatDate(checkOut, INTL_LONG_WEEKDAY)}
              </BodyText>
              <Heading variant="heading5" wrap="no-wrap">
                {intl.formatDate(checkOut, INTL_DMY_CASUAL_FORMAT)}<SmallSup>{dayOffsetText}</SmallSup>
              </Heading>
            </Group>
          </Group>
        </DateRangeContainer>

        {/* Kids Fly Free + Availability Warning */}
        <Group direction="vertical" horizontalAlign="start" gap={4}>
          {showInventoryMessage && <Group direction="horizontal" verticalAlign="center" gap={4}>
            <SolidExclamationCircleIcon colour="urgency" size="XS" />
            <BodyText variant="small" colour="critical">
              {inventoryMessage}
            </BodyText>
          </Group>}
          {bundleData.isFnfPriceApplied && (
            <MaldivesFnfSpecialOffer
              specialOfferLabel={specialOfferLabel}
              fnfInventoryMessage={fnfInventoryMessage}
              isHorizontal
            />
          )}
          {isKidsFlyFreeDate(departDate) && <KidsFlyFreeLabel variant="short" />}
          {showRewardSeatsLabel && <MaldivesCharterRewardSeatsAvailableLabel />}
        </Group>

        {/* Price and Select Button */}
        <Group direction="horizontal" horizontalAlign={fareCarouselExpanded ? 'end' : 'space-between'} verticalAlign="center" gap={12}>
          {!fareCarouselExpanded && (
            <VerticalSpacer gap={4}>
              {priceDisplay}
            </VerticalSpacer>
          )}
          <div onClick={e => e.stopPropagation()}>{selectControl}</div>
        </Group>
      </VerticalSpacer>
      {fareCarouselExpanded && allowedFareClasses && allowedFareClasses.length > 0 && (
        <MaldivesCharterDateFareCarousel
          fareClasses={allowedFareClasses}
          selectedFareClass={selectedFareClass ?? fareClass}
          isDateRowSelected={isSelected}
          checkIn={checkIn}
          checkOut={checkOut}
          nights={nights}
          rooms={rooms}
          priceMode={priceMode}
          luxPlusEnabled={luxPlusEnabled}
          tripOption={carouselTripOption}
          onPickFare={handleCarouselPickFare}
          hotelPriceByCabinAndDate={hotelPriceByCabinAndDate}
        />
      )}
    </DateCard>
  )
}

export default MaldivesDirectSearchFlightCalendarItemMobile
